import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { asyncScheduler, EMPTY, of, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Customer } from 'src/app/core/models/customer.model';
import { ConferencingService } from 'src/app/core/services/conferencing.service';
import { CustomerPresenceService } from 'src/app/core/services/customer-presence.service';
import { CustomerService } from 'src/app/core/services/customer.service';
import { GlobalPresenceService } from 'src/app/core/services/global-presence.service';
import { PusherService } from 'src/app/core/services/pusher.service';
import { UserChannelService } from 'src/app/core/services/user-channel.service';
import { State } from 'src/app/state';
import { selectSubscription } from 'src/app/state/conferencing.selectors';
import * as UserActions from './user.actions';
import * as UserSelectors from './user.selectors';
import * as PusherActions from '../../state/pusher.actions';
import * as SiteActions from '../../manager/sites/state/site.actions';
import * as ResidentActions from '../../manager/residents/state/resident.actions';
import { LoginResponse } from './user.reducers';
import { CustomerAdminService } from 'src/app/core/services/customer-admin.service';
import { Administrator } from 'src/app/core/models/administrator.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import {AllauthService} from '../../core/services/allauth.service';
import {AllAuthLoginResponse, AuthFlows, Flow, ReauthenticateResponse} from '../../core/models/allauth.model';
import {HttpErrorResponse} from '@angular/common/http';
import {AllauthAccountsService} from '../../core/services/allauth-accounts.service';
import {PreviousRouteService} from '../../core/services/previous-route.service';

/**
 * Statuses that mean the server has turned the token down for good. Anything
 * else -- a network drop, a 5xx -- is worth another try on the next poll.
 */
const TERMINAL_REFRESH_STATUSES = [400, 401, 403];

const isTerminalRefreshError = (error: HttpErrorResponse): boolean =>
  TERMINAL_REFRESH_STATUSES.includes(error?.status);

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private allauthService: AllauthService,
    private customerService: CustomerService,
    private customerAdminService: CustomerAdminService,
    private router: Router,
    private pusher: PusherService,
    private globalPresence: GlobalPresenceService,
    private customerPresence: CustomerPresenceService,
    private userChannel: UserChannelService,
    private conferencing: ConferencingService,
    private translateService: TranslateService,
    private store$: Store<State>,
    private jwtHelper: JwtHelperService,
    private allauthAccountsService: AllauthAccountsService,
    private previousRouteService: PreviousRouteService,
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.login),
      exhaustMap((action) =>
        this.allauthService.login(action.username, action.password).pipe(
          map((allAuthLoginResponse: AllAuthLoginResponse) => {
            return UserActions.loginSuccess({
              user: allAuthLoginResponse.meta.user,
              token: allAuthLoginResponse.meta.access_token,
              sessionToken: allAuthLoginResponse.meta.session_token
            });
          }),
          catchError((httpErrorResponse: HttpErrorResponse) => {
            const allAuthLoginResponse: AllAuthLoginResponse = httpErrorResponse.error;
            const pendingFlow: Flow = (allAuthLoginResponse?.data as AuthFlows)?.flows.find(flow => flow.is_pending);

            // No parsable body means the request never reached allauth (server
            // down, TLS failure, offline). Surface that instead of throwing
            // inside catchError, which would leave the form stuck pending.
            if (!allAuthLoginResponse?.status) {
              return of(UserActions.loginFailure({
                error: {
                  code: 'unreachable',
                  message: this.translateService.instant('LOGIN.FORM.ERRORS.UNREACHABLE'),
                  param: '',
                },
              }));
            }

            if (allAuthLoginResponse.status === 400) {
              return of(UserActions.loginFailure({ error: allAuthLoginResponse.errors[0] }));
            } else if (allAuthLoginResponse.status === 401 && pendingFlow) {
              switch (pendingFlow.id) {
                case 'mfa_authenticate':
                  return of(UserActions.totpLoginRedirect(),
                    UserActions.storeSessionToken({sessionToken: allAuthLoginResponse.meta.session_token}));
                case 'mfa_reauthenticate':
                  return of(UserActions.totpLoginRedirect(),
                    UserActions.storeSessionToken({sessionToken: allAuthLoginResponse.meta.session_token}));
                default:
                  break;
              }
              return of(UserActions.totpLoginRedirect());
            } else if (allAuthLoginResponse.status === 401 && allAuthLoginResponse.meta?.is_authenticated === false) {
              return of(UserActions.loginFailure({ error: {code: '401', message: 'Not Authenticated', param: ''} }));
            } else if (allAuthLoginResponse.status === 409) {
              // allauth rejects a login while a session is already active
              return of(UserActions.loginFailure({
                error: {
                  code: 'already_authenticated',
                  message: this.translateService.instant('LOGIN.FORM.ERRORS.ALREADY_AUTHENTICATED'),
                  param: '',
                },
              }));
            }
            else {
              // Not every allauth error carries an errors array; without this
              // fallback an unexpected shape throws here, no action is
              // dispatched, and the form stays stuck pending.
              return of(UserActions.loginFailure({
                error: allAuthLoginResponse.errors?.[0] ?? {
                  code: String(allAuthLoginResponse.status),
                  message: this.translateService.instant('LOGIN.FORM.ERRORS.UNEXPECTED'),
                  param: '',
                },
              }));
            }
          })
        )
      )
    )
  );

  mfaAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.mfaAuthentication),
      exhaustMap((action) =>
        this.allauthService.mfaAuthentication(action.code).pipe(
          map((allAuthLoginResponse: AllAuthLoginResponse) => {
            return UserActions.loginSuccess({
              user: allAuthLoginResponse.meta.user,
              token: allAuthLoginResponse.meta.access_token,
              sessionToken: allAuthLoginResponse.meta.session_token
            });
          }),
          catchError((httpErrorResponse: HttpErrorResponse) => {
            const allAuthLoginResponse: AllAuthLoginResponse = httpErrorResponse.error;
            const pendingFlow: Flow = (allAuthLoginResponse?.data as AuthFlows)?.flows.find(flow => flow.is_pending);

            if (allAuthLoginResponse.status === 400) {
              return of(UserActions.mfaAuthenticationFailure({ error: allAuthLoginResponse.errors[0] }));
            } else if (allAuthLoginResponse.status === 410) {
              return of(UserActions.logout());
            } else if (allAuthLoginResponse.status === 401 && pendingFlow) {
              switch (pendingFlow.id) {
                case 'mfa_authenticate':
                  return of(UserActions.totpLoginRedirect(),
                    UserActions.storeSessionToken({sessionToken: allAuthLoginResponse.meta.session_token}));
                case 'mfa_reauthenticate':
                  return of(UserActions.totpLoginRedirect(),
                    UserActions.storeSessionToken({sessionToken: allAuthLoginResponse.meta.session_token}));
                default:
                  break;
              }
              return of(UserActions.totpLoginRedirect());
            } else if (allAuthLoginResponse.status === 401) {
              return of(UserActions.mfaAuthenticationFailure({ error: {code: '401', message: 'Not Authenticated', param: ''} }));
            }
            else {
              return of(UserActions.mfaAuthenticationFailure({ error: allAuthLoginResponse.errors[0] }));
            }
          })
        )
      )
    )
  );

  redirectToTotpLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.totpLoginRedirect),
        map((action) => this.router.navigate(['login/totp']))
      ),
    { dispatch: false }
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.loginSuccess),
        tap((action) => {
          this.store$.dispatch(PusherActions.connectToPusher());
          switch (action.user.user_type) {
            case 'staff':
              this.router.navigate(['/superuser']);
              break;
            case 'customeradmin':
              if (this.router.url === '/login' || this.router.url === '/login/totp') {
                this.router.navigate(['/manager/dashboard/overview']);
              }
              break;
            case 'supporter':
              alert('Supporter accounts are not yet supported here!');
              break;
          }
        })
      ),
    { dispatch: false }
  );

  $logout = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.logout),
        withLatestFrom(this.store$.select(selectSubscription)),
        tap(([action, subscription]) => {
          // do logout stuff here
          // unsubscribe websocket channels?
          this.globalPresence.unsubscribe();
          this.customerPresence.unsubscribe();
          if (subscription) {
            this.conferencing.unsubscribe(subscription.channelName);
          }
          // TODO: Unsubscribe message box channel for all sites
          // this.messageboxService.unsubscribe();

          this.userChannel.unsubscribe();
          this.pusher.disconnect();
          // cleanup local storage
          localStorage.clear();
          // navigate to login
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  $logoutSession = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.logoutSession),
        switchMap(() =>
          this.allauthService.logout().pipe(
            map((response) => {
              // Handle successful logout response if needed
              return response;
            })
          )
        )
      ),
    { dispatch: false }
  );

  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.loginRedirect),
        tap((action) => this.router.navigate(['/login']))
      ),
    { dispatch: false }
  );

  reauthenticateRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.reauthenticateRedirect),
        tap((action) => this.router.navigate(['/login/reauthenticate']))
      ),
    { dispatch: false }
  );

  reauthenticate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.reauthenticate),
      exhaustMap((action) =>
        this.allauthAccountsService.reauthenticate(action.password).pipe(
          map((reauthenticateResponse: ReauthenticateResponse) => {
            return UserActions.reauthenticateSuccess(reauthenticateResponse);
          }),
          catchError((httpErrorResponse: HttpErrorResponse) => {
            const allAuthLoginResponse: AllAuthLoginResponse = httpErrorResponse.error;
            return of(UserActions.reauthenticateFailure({ error: allAuthLoginResponse.errors[0] }));
          })
        )
      )
    )
  );

  reauthenticateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.reauthenticateSuccess),
        map((action) => {
          const previousUrl = this.previousRouteService.getPreviousUrl();
          console.log(`previous URL: ${previousUrl}`);
          if (previousUrl) {
            this.router.navigate([previousUrl]);
          }
        })
      ),
    { dispatch: false }
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changePassword),
      exhaustMap((action) =>
        this.allauthAccountsService.changePassword(action.oldPassword, action.newPassword).pipe(
          map((response: AllAuthLoginResponse) => {
            return UserActions.changePasswordSuccess(response);
          }),
          catchError((httpErrorResponse: HttpErrorResponse) => {
            const allAuthLoginResponse: AllAuthLoginResponse = httpErrorResponse.error;
            return of(UserActions.changePasswordFailure({ error: allAuthLoginResponse.errors[0] }));
          })
        )
      )
    )
  );

  setLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.loginSuccess),
        map((action) =>
          this.translateService.setDefaultLang(action.user.locale)
        )
      ),
    { dispatch: false }
  );

  getCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loginSuccess),
      exhaustMap((action) =>
        this.customerService.getCustomer(action.user.customer).pipe(
          map((customer: Customer) => {
            return UserActions.getCustomerSuccess(customer);
          }),
          catchError((error) => of(UserActions.getCustomerFailure({ error })))
        )
      )
    )
  );

  getCustomerPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerSuccess),
      exhaustMap((customer: Customer) =>
        this.customerService.getMyPermissionsOnCustomer(customer.id).pipe(
          map((permissions: string[]) => {
            return UserActions.getCustomerPermissionsSuccess({ permissions });
          }),
          catchError((error) =>
            of(UserActions.getCustomerPermissionsFailure({ error }))
          )
        )
      )
    )
  );

  getSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerSuccess),
      map((action) => SiteActions.getSites())
    )
  );

  getResidents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerSuccess),
      map((action) => ResidentActions.getResidents())
    )
  );

  getAdministrators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerSuccess),
      exhaustMap((customer: Customer) =>
        this.customerAdminService
          .getAdministratorsForCustomer(customer.id)
          .pipe(
            map((administrators: Administrator[]) => {
              return UserActions.getAdministratorsSuccess({ administrators });
            }),
            catchError((error) =>
              of(UserActions.getAdministratorsFailure({ error }))
            )
          )
      )
    )
  );

  checkTokenAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loginSuccess),
      map((action) => {
        if (this.jwtHelper.isTokenExpired(action.token)) {
          return UserActions.loginRedirect();
        } else {
          return UserActions.refreshToken();
        }
      })
    )
  );

  refreshToken$ = createEffect(
    () =>
      ({ scheduler = asyncScheduler, stopTimer = EMPTY } = {}) =>
        this.actions$.pipe(
          ofType(UserActions.refreshToken),
          withLatestFrom(
            this.store$.select(UserSelectors.selectTokenRefreshInterval)
          ),
          switchMap(([action, pollingInterval]) =>
            timer(0, pollingInterval, scheduler).pipe(
              takeUntil(stopTimer),
              // Read the token on every tick rather than once up front: each
              // refresh issues a new token, and the server measures the refresh
              // window from the original token's orig_iat, so re-sending the
              // login token forever would eventually fail with
              // 'Refresh has expired.'
              withLatestFrom(this.store$.select(UserSelectors.selectToken)),
              switchMap(([, token]) =>
                this.allauthService.getNewJwt(token).pipe(
                  map((response: LoginResponse) =>
                    UserActions.refreshTokenSuccess({
                      token: response.token,
                    })
                  ),
                  catchError((error: HttpErrorResponse) =>
                    of(UserActions.refreshTokenFailure({ error }))
                  )
                )
              ),
              // A token the server has rejected will be rejected again, so stop
              // polling once that happens and let refreshTokenFailure$ end the
              // session. Transient failures fall through and are retried.
              takeWhile(
                (result) =>
                  !(
                    result.type === UserActions.refreshTokenFailure.type &&
                    isTerminalRefreshError(result.error)
                  ),
                true
              )
            )
          )
        )
  );

  refreshTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.refreshTokenFailure),
      filter((action) => isTerminalRefreshError(action.error)),
      // The session cannot be revived, so tear it down rather than leaving the
      // user in a shell that quietly fails every subsequent request.
      map(() => UserActions.logout())
    )
  );
}
